import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { WinFeedback } from "@/components/feedback/WinFeedback";
import {
  Home, Pill, CalendarDays, Activity, Mic, FileText, Lock, Inbox,
  Heart, CreditCard, MonitorSmartphone, UserPlus, LogOut, Menu, ShieldCheck, Settings,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/app", icon: Home, label: "Home", end: true, testid: "nav-home" },
  { to: "/app/meds", icon: Pill, label: "Medications", testid: "nav-meds" },
  { to: "/app/calendar", icon: CalendarDays, label: "Calendar", testid: "nav-calendar" },
  { to: "/app/vitals", icon: Activity, label: "Vitals", testid: "nav-vitals" },
  { to: "/app/scribe", icon: Mic, label: "Visit scribe", testid: "nav-scribe" },
  { to: "/app/claims", icon: FileText, label: "Claims & appeals", testid: "nav-claims" },
];

const NAV_MORE = [
  { to: "/app/patients", icon: Heart, label: "Care recipients", testid: "nav-patients" },
  { to: "/app/inbox", icon: Inbox, label: "Inbox", testid: "nav-inbox" },
  { to: "/app/vault", icon: Lock, label: "Document vault", testid: "nav-vault" },
  { to: "/app/invite", icon: UserPlus, label: "Invite member", testid: "nav-invite" },
  { to: "/app/privacy", icon: ShieldCheck, label: "Privacy & consent", testid: "nav-privacy" },
  { to: "/app/billing", icon: CreditCard, label: "Plans & billing", testid: "nav-billing" },
  { to: "/app/sessions", icon: MonitorSmartphone, label: "Sessions", testid: "nav-sessions" },
  { to: "/app/account", icon: Settings, label: "Account & settings", testid: "nav-account" },
];

function NavItem({ item, onClick, unread }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClick}
      data-testid={item.testid}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150",
          isActive ? "bg-brand text-white" : "text-brand-tint/90 hover:bg-white/10"
        )
      }
    >
      <Icon className="w-[18px] h-[18px] shrink-0" />
      <span className="flex-1">{item.label}</span>
      {unread > 0 && (
        <span className="bg-mint text-brand-dark text-[10px] font-bold rounded-full px-1.5 min-w-[18px] text-center">
          {unread > 9 ? "9+" : unread}
        </span>
      )}
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, role, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.messagesUnreadCount().then((r) => setUnread(r?.unread ?? 0)).catch(() => {});
  }, []);

  const doSignOut = async () => { await signOut(); navigate("/login"); };
  const close = () => setMobileOpen(false);

  const sidebar = (
    <div className="flex flex-col h-full bg-brand-dark text-white w-64">
      <div className="px-4 py-5 border-b border-white/10">
        <Logo className="h-9" onDark />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {NAV.map((n) => <NavItem key={n.to} item={n} onClick={close} unread={n.to === "/app/inbox" ? unread : 0} />)}
        <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-tint/50 px-3 pt-5 pb-1">Circle & tools</div>
        {NAV_MORE.map((n) => <NavItem key={n.to} item={n} onClick={close} unread={n.to === "/app/inbox" ? unread : 0} />)}
      </nav>
      <div className="border-t border-white/10 p-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-mint flex items-center justify-center text-brand-dark font-semibold">
            {user?.name?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-[11px] text-brand-tint/70 truncate">{role ? role.replace(/_/g, " ") : user?.email}</div>
          </div>
        </div>
        <button
          onClick={doSignOut}
          data-testid="signout-button"
          className="mt-1 w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-brand-tint/90 hover:bg-white/10 transition-colors"
        >
          <LogOut className="w-[18px] h-[18px]" /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-surface">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block sticky top-0 h-screen shrink-0">{sidebar}</aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/40" onClick={close} />
          <div className="relative z-10 h-full">{sidebar}</div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden sticky top-0 z-30 bg-brand-dark text-white flex items-center gap-3 px-4 py-3">
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
            <Menu className="w-6 h-6" />
          </button>
          <Logo className="h-7" onDark />
        </header>
        <main className="flex-1 max-w-4xl w-full mx-auto px-5 sm:px-8 py-6 sm:py-10">
          {children}
        </main>
        <WinFeedback />
      </div>
    </div>
  );
}
